import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, MessageCircle, Mail, CheckCircle, XCircle, Loader2, MessageSquare, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettings() {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testingWpp, setTestingWpp] = useState(false);
    const [testingSmtp, setTestingSmtp] = useState(false);
    const [wppResult, setWppResult] = useState(null);
    const [smtpResult, setSmtpResult] = useState(null);

    // Password form
    const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [pwSaving, setPwSaving] = useState(false);

    // WPP form
    const [wppForm, setWppForm] = useState({ wppconnect_url: '', wppconnect_session: '', wppconnect_token: '' });

    // WhatsApp form
    const [whatsappForm, setWhatsappForm] = useState({ store_phone: '' });

    // SMTP form
    const [smtpForm, setSmtpForm] = useState({ smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', smtp_from_email: '' });

    // Cobros / Uala form
    const [cobrosForm, setCobrosForm] = useState({
        uala_token: '', uala_alias: '', uala_api_url: '', uala_notification_url: '',
        store_name: '', public_base_url: '',
    });

    useEffect(() => {
        api.get('/admin/settings').then(r => {
            setSettings(r.data);
            setWppForm({
                wppconnect_url: r.data.wppconnect_url || '',
                wppconnect_session: r.data.wppconnect_session || '',
                wppconnect_token: '',
            });
            setWhatsappForm({ store_phone: r.data.store_phone || '' });
            setSmtpForm({
                smtp_host: r.data.smtp_host || '',
                smtp_port: r.data.smtp_port || 587,
                smtp_user: r.data.smtp_user || '',
                smtp_password: '',
                smtp_from_email: r.data.smtp_from_email || '',
            });
            setCobrosForm({
                uala_token: '',
                uala_alias: r.data.uala_alias || '',
                uala_api_url: r.data.uala_api_url || '',
                uala_notification_url: r.data.uala_notification_url || '',
                store_name: r.data.store_name || '',
                public_base_url: r.data.public_base_url || '',
            });
        }).catch(e => console.error(e)).finally(() => setLoading(false));
    }, []);

    const handlePasswordChange = async () => {
        if (!pwForm.current_password || !pwForm.new_password) { toast.error('Completa todos los campos'); return; }
        if (pwForm.new_password !== pwForm.confirm) { toast.error('Las contrasenas no coinciden'); return; }
        setPwSaving(true);
        try {
            await api.post('/admin/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
            toast.success('Contrasena actualizada');
            setPwForm({ current_password: '', new_password: '', confirm: '' });
        } catch { toast.error('Error: contrasena actual incorrecta'); }
        setPwSaving(false);
    };

    const handleWppSave = async () => {
        setSaving(true);
        const payload = { ...wppForm };
        if (!payload.wppconnect_token) delete payload.wppconnect_token;
        try {
            await api.put('/admin/settings', payload);
            toast.success('WPPConnect configurado');
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleWhatsAppSave = async () => {
        if (!whatsappForm.store_phone) { toast.error('Ingresa un numero de WhatsApp'); return; }
        setSaving(true);
        try {
            await api.put('/admin/settings', { store_phone: whatsappForm.store_phone });
            toast.success('Numero de WhatsApp actualizado');
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleSmtpSave = async () => {
        setSaving(true);
        const payload = { ...smtpForm, smtp_port: Number(smtpForm.smtp_port) };
        if (!payload.smtp_password) delete payload.smtp_password;
        try {
            await api.put('/admin/settings', payload);
            toast.success('SMTP configurado');
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleCobrosSave = async () => {
        setSaving(true);
        const payload = { ...cobrosForm };
        if (!payload.uala_token) delete payload.uala_token;
        try {
            await api.put('/admin/settings', payload);
            toast.success('Configuracion de cobros guardada');
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const testWpp = async () => {
        setTestingWpp(true); setWppResult(null);
        try {
            const r = await api.post('/admin/test-wppconnect');
            setWppResult(r.data);
        } catch (e) { setWppResult({ status: 'error', message: e.response?.data?.detail || 'Error de conexion' }); }
        setTestingWpp(false);
    };

    const testSmtp = async () => {
        setTestingSmtp(true); setSmtpResult(null);
        try {
            const r = await api.post('/admin/test-smtp');
            setSmtpResult(r.data);
        } catch (e) { setSmtpResult({ status: 'error', message: e.response?.data?.detail || 'Error de conexion' }); }
        setTestingSmtp(false);
    };

    return (
        <div className="space-y-6" data-testid="admin-settings">
            <h1 className="text-2xl font-heading font-bold text-cp-text-main">Configuracion</h1>

            <Tabs defaultValue="cobros" className="w-full">
                <TabsList className="bg-cp-surface mb-6 flex-wrap h-auto gap-1" data-testid="settings-tabs">
                    <TabsTrigger value="cobros" className="data-[state=active]:bg-white"><CreditCard size={14} className="mr-1.5" />Cobros</TabsTrigger>
                    <TabsTrigger value="account" className="data-[state=active]:bg-white"><Lock size={14} className="mr-1.5" />Cuenta</TabsTrigger>
                    <TabsTrigger value="whatsapp" className="data-[state=active]:bg-white"><MessageSquare size={14} className="mr-1.5" />WhatsApp</TabsTrigger>
                    <TabsTrigger value="wppconnect" className="data-[state=active]:bg-white"><MessageCircle size={14} className="mr-1.5" />WPPConnect</TabsTrigger>
                    <TabsTrigger value="smtp" className="data-[state=active]:bg-white"><Mail size={14} className="mr-1.5" />SMTP</TabsTrigger>
                </TabsList>

                {/* Cobros / Uala */}
                <TabsContent value="cobros">
                    <div className="bg-white border border-cp-border rounded-xl p-6 max-w-lg space-y-6" data-testid="settings-cobros">
                        <div>
                            <h2 className="font-heading font-bold text-cp-text-main mb-1">Cobros Online - Uala Bis</h2>
                            <p className="text-cp-text-muted text-sm">Configura tu pasarela Uala para generar links de pago automaticos con cada pedido.</p>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm mb-1.5 block">Token API Uala {settings.has_uala_token && <span className="text-cp-success text-xs">(configurado)</span>}</Label>
                                <Input
                                    type="password"
                                    value={cobrosForm.uala_token}
                                    onChange={e => setCobrosForm({...cobrosForm, uala_token: e.target.value})}
                                    placeholder={settings.has_uala_token ? 'Dejar vacio para mantener' : 'Bearer token de Uala Bis'}
                                    data-testid="uala-token"
                                />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Alias / CBU para transferencias</Label>
                                <Input
                                    value={cobrosForm.uala_alias}
                                    onChange={e => setCobrosForm({...cobrosForm, uala_alias: e.target.value})}
                                    placeholder="ej: cloudpet.uala o CVU/CBU"
                                    data-testid="uala-alias"
                                />
                                <p className="text-xs text-cp-text-muted mt-1">Se muestra en el PDF como alternativa de pago</p>
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">URL API Uala</Label>
                                <Input
                                    value={cobrosForm.uala_api_url}
                                    onChange={e => setCobrosForm({...cobrosForm, uala_api_url: e.target.value})}
                                    placeholder="https://merchant.prod.ualabis.com.ar"
                                    data-testid="uala-api-url"
                                />
                                <p className="text-xs text-cp-text-muted mt-1">Dejar vacio para usar el valor por defecto</p>
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">URL de notificacion webhook</Label>
                                <Input
                                    value={cobrosForm.uala_notification_url}
                                    onChange={e => setCobrosForm({...cobrosForm, uala_notification_url: e.target.value})}
                                    placeholder="https://tu-dominio.com/api/webhooks/uala"
                                    data-testid="uala-webhook"
                                />
                            </div>
                        </div>

                        <div>
                            <h3 className="font-semibold text-cp-text-main text-sm mb-3">Configuracion de la Tienda (para PDFs)</h3>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm mb-1.5 block">Nombre de la tienda</Label>
                                    <Input
                                        value={cobrosForm.store_name}
                                        onChange={e => setCobrosForm({...cobrosForm, store_name: e.target.value})}
                                        placeholder="CloudPet"
                                        data-testid="store-name"
                                    />
                                </div>
                                <div>
                                    <Label className="text-sm mb-1.5 block">URL publica del sitio</Label>
                                    <Input
                                        value={cobrosForm.public_base_url}
                                        onChange={e => setCobrosForm({...cobrosForm, public_base_url: e.target.value})}
                                        placeholder="https://tu-dominio.com"
                                        data-testid="public-base-url"
                                    />
                                    <p className="text-xs text-cp-text-muted mt-1">Se usa como URL de retorno en el pago</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleCobrosSave}
                            disabled={saving}
                            data-testid="save-cobros-btn"
                            className="bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
                        >
                            {saving ? 'Guardando...' : 'Guardar Configuracion'}
                        </button>
                    </div>
                </TabsContent>

                {/* Account */}
                <TabsContent value="account">
                    <div className="bg-white border border-cp-border rounded-xl p-6 max-w-md" data-testid="settings-account">
                        <h2 className="font-heading font-bold text-cp-text-main mb-4">Cambiar Contrasena</h2>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm mb-1.5 block">Contrasena actual</Label>
                                <Input type="password" value={pwForm.current_password}
                                    onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
                                    data-testid="current-password" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Nueva contrasena</Label>
                                <Input type="password" value={pwForm.new_password}
                                    onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
                                    data-testid="new-password" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Confirmar nueva contrasena</Label>
                                <Input type="password" value={pwForm.confirm}
                                    onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                                    data-testid="confirm-password" />
                            </div>
                            <button onClick={handlePasswordChange} disabled={pwSaving}
                                data-testid="save-password-btn"
                                className="bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white px-6 py-2.5 rounded-xl text-sm font-semibold">
                                {pwSaving ? 'Guardando...' : 'Cambiar Contrasena'}
                            </button>
                        </div>
                    </div>
                </TabsContent>

                {/* WhatsApp */}
                <TabsContent value="whatsapp">
                    <div className="bg-white border border-cp-border rounded-xl p-6 max-w-lg" data-testid="settings-whatsapp">
                        <h2 className="font-heading font-bold text-cp-text-main mb-1">Boton Flotante de WhatsApp</h2>
                        <p className="text-cp-text-muted text-sm mb-4">Configura el numero de WhatsApp que aparecera en el boton flotante de la tienda.</p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm mb-1.5 block">Numero de WhatsApp</Label>
                                <Input
                                    value={whatsappForm.store_phone}
                                    onChange={e => setWhatsappForm({...whatsappForm, store_phone: e.target.value})}
                                    placeholder="+54 911 1234 5678 o +5491112345678"
                                    data-testid="whatsapp-phone"
                                />
                                <p className="text-cp-text-muted text-xs mt-1.5">Incluye el codigo de pais (ej: +54 para Argentina)</p>
                            </div>
                            <button
                                onClick={handleWhatsAppSave}
                                disabled={saving}
                                data-testid="save-whatsapp-btn"
                                className="bg-[#25D366] hover:bg-[#20BA5A] disabled:opacity-60 text-white px-6 py-2.5 rounded-xl text-sm font-semibold"
                            >
                                {saving ? 'Guardando...' : 'Guardar Numero'}
                            </button>
                        </div>
                    </div>
                </TabsContent>

                {/* WPPConnect */}
                <TabsContent value="wppconnect">
                    <div className="bg-white border border-cp-border rounded-xl p-6 max-w-lg" data-testid="settings-wppconnect">
                        <h2 className="font-heading font-bold text-cp-text-main mb-1">Configuracion WPPConnect</h2>
                        <p className="text-cp-text-muted text-sm mb-4">Configura tu servidor WPPConnect para enviar notificaciones por WhatsApp.</p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-sm mb-1.5 block">URL del servidor</Label>
                                <Input value={wppForm.wppconnect_url} onChange={e => setWppForm({...wppForm, wppconnect_url: e.target.value})}
                                    placeholder="https://tu-servidor-wppconnect.com" data-testid="wpp-url" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Nombre de sesion</Label>
                                <Input value={wppForm.wppconnect_session} onChange={e => setWppForm({...wppForm, wppconnect_session: e.target.value})}
                                    placeholder="mi-sesion" data-testid="wpp-session" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Token de autorizacion {settings.has_wppconnect_token && <span className="text-cp-success text-xs">(configurado)</span>}</Label>
                                <Input type="password" value={wppForm.wppconnect_token} onChange={e => setWppForm({...wppForm, wppconnect_token: e.target.value})}
                                    placeholder={settings.has_wppconnect_token ? 'Dejar vacio para mantener' : 'Bearer token'} data-testid="wpp-token" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleWppSave} disabled={saving}
                                    data-testid="save-wpp-btn"
                                    className="bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white px-6 py-2.5 rounded-xl text-sm font-semibold">
                                    {saving ? 'Guardando...' : 'Guardar'}
                                </button>
                                <button onClick={testWpp} disabled={testingWpp}
                                    data-testid="test-wpp-btn"
                                    className="bg-cp-accent hover:bg-cp-accent-dark text-cp-text-main px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2">
                                    {testingWpp ? <Loader2 size={14} className="animate-spin" /> : null}
                                    Probar Conexion
                                </button>
                            </div>
                            {wppResult && (
                                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${wppResult.status === 'connected' ? 'bg-cp-success/10 text-cp-success' : 'bg-cp-error/10 text-cp-error'}`}
                                    data-testid="wpp-result">
                                    {wppResult.status === 'connected' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                    {wppResult.message}
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* SMTP */}
                <TabsContent value="smtp">
                    <div className="bg-white border border-cp-border rounded-xl p-6 max-w-lg" data-testid="settings-smtp">
                        <h2 className="font-heading font-bold text-cp-text-main mb-1">Configuracion SMTP</h2>
                        <p className="text-cp-text-muted text-sm mb-4">Configura el servidor de correo para notificaciones por email.</p>
                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-2">
                                    <Label className="text-sm mb-1.5 block">Host SMTP</Label>
                                    <Input value={smtpForm.smtp_host} onChange={e => setSmtpForm({...smtpForm, smtp_host: e.target.value})}
                                        placeholder="smtp.gmail.com" data-testid="smtp-host" />
                                </div>
                                <div>
                                    <Label className="text-sm mb-1.5 block">Puerto</Label>
                                    <Input type="number" value={smtpForm.smtp_port} onChange={e => setSmtpForm({...smtpForm, smtp_port: e.target.value})}
                                        data-testid="smtp-port" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Usuario</Label>
                                <Input value={smtpForm.smtp_user} onChange={e => setSmtpForm({...smtpForm, smtp_user: e.target.value})}
                                    data-testid="smtp-user" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Contrasena {settings.has_smtp_password && <span className="text-cp-success text-xs">(configurada)</span>}</Label>
                                <Input type="password" value={smtpForm.smtp_password} onChange={e => setSmtpForm({...smtpForm, smtp_password: e.target.value})}
                                    placeholder={settings.has_smtp_password ? 'Dejar vacio para mantener' : ''} data-testid="smtp-password" />
                            </div>
                            <div>
                                <Label className="text-sm mb-1.5 block">Email remitente</Label>
                                <Input type="email" value={smtpForm.smtp_from_email} onChange={e => setSmtpForm({...smtpForm, smtp_from_email: e.target.value})}
                                    placeholder="cloudpet@tudominio.com" data-testid="smtp-from" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleSmtpSave} disabled={saving}
                                    data-testid="save-smtp-btn"
                                    className="bg-cp-secondary hover:bg-cp-secondary-hover disabled:opacity-60 text-white px-6 py-2.5 rounded-xl text-sm font-semibold">
                                    {saving ? 'Guardando...' : 'Guardar'}
                                </button>
                                <button onClick={testSmtp} disabled={testingSmtp}
                                    data-testid="test-smtp-btn"
                                    className="bg-cp-accent hover:bg-cp-accent-dark text-cp-text-main px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2">
                                    {testingSmtp ? <Loader2 size={14} className="animate-spin" /> : null}
                                    Probar Conexion
                                </button>
                            </div>
                            {smtpResult && (
                                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${smtpResult.status === 'connected' ? 'bg-cp-success/10 text-cp-success' : 'bg-cp-error/10 text-cp-error'}`}
                                    data-testid="smtp-result">
                                    {smtpResult.status === 'connected' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                                    {smtpResult.message}
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
